/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { identityProviderConnectionsSyncApplications } from "../funcs/identityProviderConnectionsSyncApplications.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { OktaIdentityProviderConnection } from "../models/components/oktaidentityproviderconnection.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SyncIdentityProviderConnectionApplicationsRequest,
  SyncIdentityProviderConnectionApplicationsSecurity,
} from "../models/operations/syncidentityproviderconnectionapplications.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SyncIdentityProviderConnectionApplicationsMutationVariables = {
  request: SyncIdentityProviderConnectionApplicationsRequest;
  security?: SyncIdentityProviderConnectionApplicationsSecurity | undefined;
  options?: RequestOptions;
};

export type SyncIdentityProviderConnectionApplicationsMutationData =
  OktaIdentityProviderConnection;

export type SyncIdentityProviderConnectionApplicationsMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * syncApplications identityProviderConnections
 *
 * @remarks
 * Run the applications snapshot on the next coordinator pass, within minutes, instead of at the next scheduled interval. The connection must be verified. Rate limited per organization. Requires org:admin.
 */
export function useSyncIdentityProviderConnectionApplicationsMutation(
  options?: MutationHookOptions<
    SyncIdentityProviderConnectionApplicationsMutationData,
    SyncIdentityProviderConnectionApplicationsMutationError,
    SyncIdentityProviderConnectionApplicationsMutationVariables
  >,
): UseMutationResult<
  SyncIdentityProviderConnectionApplicationsMutationData,
  SyncIdentityProviderConnectionApplicationsMutationError,
  SyncIdentityProviderConnectionApplicationsMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSyncIdentityProviderConnectionApplicationsMutation(client, options),
    ...options,
  });
}

export function mutationKeySyncIdentityProviderConnectionApplications(): MutationKey {
  return ["@gram/client", "identityProviderConnections", "syncApplications"];
}

export function buildSyncIdentityProviderConnectionApplicationsMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SyncIdentityProviderConnectionApplicationsMutationVariables,
  ) => Promise<SyncIdentityProviderConnectionApplicationsMutationData>;
} {
  return {
    mutationKey: mutationKeySyncIdentityProviderConnectionApplications(),
    mutationFn: function syncIdentityProviderConnectionApplicationsMutationFn({
      request,
      security,
      options,
    }): Promise<SyncIdentityProviderConnectionApplicationsMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(identityProviderConnectionsSyncApplications(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
